import { AfterViewInit, ChangeDetectionStrategy, Component, inject, input, output, viewChild } from '@angular/core';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { GeneralLookupService } from '@app/cem/core-data/services/general-lookup.service';
import { AppStorage } from '@app/common/services/data-api/app-storage.service';
import { ProjectTypeId } from '@app/shared/data/project-enums';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { AgGridAngular } from 'ag-grid-angular';
import { FilterChangedEvent, SelectionChangedEvent } from 'ag-grid-community';
import { assetMappingPipelineGridOptions, assetMappingPipelineSelectedGridOptions } from './columns';

@Component({
  selector: 'app-project-asset-mapping-pipeline',
  templateUrl: './project-asset-mapping-pipeline.component.html',
  styleUrls: ['./project-asset-mapping-pipeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ButtonDirective,
    MatDrawer,
    MatDrawerContainer,
  ],
})
export class ProjectAssetMappingPipelineComponent implements AfterViewInit {
  public generalLookupService = inject(GeneralLookupService);
  public AppStorage = inject(AppStorage);

  readonly projectTypeId = input<number>(undefined);
  readonly pipelineAssets = output<any>();

  private readonly agSelectionGrid = viewChild<AgGridAngular>('agSelectionGrid');
  private readonly agDataGrid = viewChild<AgGridAngular>('agDataGrid');
  public readonly drawer = viewChild<MatDrawer>('drawer');

  public assetMappingPipelineGridOptions = assetMappingPipelineGridOptions;
  public assetMappingPipelineSelectedGridOptions = assetMappingPipelineSelectedGridOptions;

  public availableWells: any[] = [];
  public selectedWells: any[] = [];

  ngAfterViewInit(): void {
    this.drawer().toggle();
  }

  onAvailableGridReady(params) {
    let assetTeamId = this.AppStorage.knownStore.assetTeam.AssetTeamId;
    let scenarioId = this.AppStorage.knownStore.scenario.ScenarioId;
    let selectFilter = '&$select=WellId,AssetId,AssetName,FieldDevelopmentArea,AreaTeam,Ssn,SpudDate,RigReleaseDate,IsJointVenture,PutOnProductionDate,ActualDrillGroupReference';
    let filter = `not%20contains(ProjectTypes,%20%27[${this.projectTypeId()}]%27)`;
    if (this.projectTypeId() == ProjectTypeId.Flowline) {
      filter += ` and DrillGroupId ne null`;
    }
    filter += ` and AssetTeamId eq ${assetTeamId}`;
    if (scenarioId) {
      filter += ` and ScenarioId eq ${scenarioId}`;
    }

    let qry = `WellsAssetMappingView?${selectFilter}&$filter=${filter}`;
    this.generalLookupService.getFiltered(qry)
      .then(payload => {
        this.agSelectionGrid().api.setGridOption('rowData', payload.value);
      });
  }

  onSelectedGridReady(params) {
    params.api.setGridOption('rowData', []);
  }

  onAvailableFilterChanged(event: FilterChangedEvent) {
    event.api.setGridOption('rowSelection', {
      mode: 'multiRow',
      headerCheckbox: true,
      selectAll: event.api.isAnyFilterPresent() ? 'filtered' : 'all',
    });
  }

  onAvailableSelectionChanged(params: SelectionChangedEvent) {
    this.availableWells = [];
    this.agSelectionGrid().api.getSelectedRows().map((x) => this.availableWells.push(x));
  }

  clearAvailableRows() {
    this.agSelectionGrid().api.deselectAll();
    this.availableWells = [];
  }

  clearSelectedRows() {
    this.agDataGrid().api.deselectAll();
    this.selectedWells = [];
  }

  onSelectedSelectionChanged(params: SelectionChangedEvent) {
    this.selectedWells = [];
    this.agDataGrid().api.getSelectedRows().map((x) => this.selectedWells.push(x));
  }

  addSelectedRows() {
    const selectedRows = this.agSelectionGrid().api.getSelectedRows();
    this.agDataGrid().api.applyTransaction({ add: selectedRows });
    this.agSelectionGrid().api.applyTransaction({ remove: selectedRows });
    this.agSelectionGrid().api.deselectAll();
    this.emitData();
    this.availableWells = [];
  }

  removeSelectedRows() {
    const selectedRows = this.agDataGrid().api.getSelectedRows();
    this.agSelectionGrid().api.applyTransaction({ add: selectedRows });
    this.agDataGrid().api.applyTransaction({ remove: selectedRows });
    this.agDataGrid().api.deselectAll();
    this.emitData();
    this.selectedWells = [];
  }

  emitData() {
    let data = [];
    this.agDataGrid().api.forEachNode(node => data.push(node.data));
    this.pipelineAssets.emit(data);
  }
}